<template>
  <div :class="$style.form">
    <header :class="[$style.form__header, $style.header]">
      <h3 :class="$style.header__title">このユーザーにアプローチする</h3>
    </header>
    <p :class="$style.form__description">
      ユーザーの情報や診断結果をもとに、ユーザーにアプローチをすることができます。ここでユーザーに送るアプローチのメッセージによって、ユーザーはどのFPに相談するかを判断します。ユーザー1人につきアプローチは1回のみ可能です。的確なアプローチのメッセージを送って、ユーザーとのマッチングに繋げましょう。
    </p>

    <p :class="[$style.form__replace, $style.replace]">
      💡 メッセージ中の<code>{{ REPLACE_NAME_VARIABLES.name }}</code
      >という文字列が、ユーザーの名前に置き換えられます。<br />
      反映は送信確認画面でご確認ください。
    </p>

    <KartePlannerCommentTemplateSelector
      :class="$style.form__template"
      :body="localPlannerComment"
      :selections="localPlannerSelections"
      @load="handleLoadTemplate"
      @edit="isEditingTemplate = true"
      @finishEdit="isEditingTemplate = false"
    />

    <template v-if="!isEditingTemplate">
      <DolphinTextarea
        :class="$style.form__textarea"
        :disabled="!isFormAvailable"
        :autoGrow="true"
        :rows="8"
        v-model="localPlannerComment"
        :placeholder="placeholder"
      />
      <MessageAttachmentActions
        :class="$style.form__selections"
        v-model="localPlannerSelections"
        :isDeleteAllSelectionsAllowed="false"
      />
      <FlatButton
        :class="$style.form__button"
        variant="primary"
        size="medium"
        @click="postComment"
      >
        アプローチする
      </FlatButton>
    </template>
    <section
      v-if="!isFormAvailable"
      :class="[$style.cover__notice, $style.notice]"
    >
      <p :class="$style.notice__text">
        <template
          v-if="
            unavailableReason ===
            KartePlannerCommentUnavailableReason.ReachedToMonthlyLimit
          "
        >
          <p>今月のアプローチ数の上限に達しました。</p>
          <p v-if="planVersion === '2022_1'">
            このユーザーにアプローチするには「Proプラン」をお申し込みください。
          </p>
          <p v-if="planVersion === '2023_1'">
            このユーザーにアプローチするには「マスタープラン」をお申し込みください。
          </p>
          <p v-else>
            このユーザーにアプローチするには「Proプラン」「Entryプラン」<br />
            または「プレミアムプラン」をお申し込みください。
          </p>
          <!-- FIXME: CSチームにフォームを作ってもらったら表示させること -->
          <!-- お申込みは<ExternalLink
              href="https://survey.zohopublic.com/zs/47Csnz"
              >こちら</ExternalLink
            > -->
        </template>
        <template
          v-else-if="
            unavailableReason === KartePlannerCommentUnavailableReason.HasPrPlan
          "
        >
          PRプランではユーザーへのアプローチは行えません。<br />
          ユーザーにアプローチするには「Entryプラン」または「Proプラン」をお申し込みください。<br />
          お申込みは<ExternalLink href="https://survey.zohopublic.com/zs/47Csnz"
            >こちら</ExternalLink
          >
        </template>
        <template
          v-else-if="
            unavailableReason ===
            KartePlannerCommentUnavailableReason.HasManageOnlyFreePlan
          "
        >
          管理者専用無料プランではユーザーへのアプローチは行えません。<br />
        </template>
        <template
          v-else-if="
            unavailableReason ===
            KartePlannerCommentUnavailableReason.BeforeContractPeriod
          "
        >
          プラン開始前です。プラン開始までお待ちください。
        </template>
        <template
          v-else-if="
            unavailableReason ===
            KartePlannerCommentUnavailableReason.AlreadyMatched
          "
        >
          すでにマッチしているユーザーへのアプローチは行えません。<br />
          <RouterLink :to="{ name: 'ownChatIndex' }">チャットルーム</RouterLink
          >をご確認ください。
        </template>
        <template v-else>
          アプローチすることができません。<br />
          オカネコ 運営事務局(
          <a href="mailto:jimukyoku@okane-kenko.jp">jimukyoku@okane-kenko.jp</a>
          )へお問い合わせください。
        </template>
      </p>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

import DolphinTextarea from '@/components/atoms/DolphinTextarea.vue'
import ExternalLink from '@/components/atoms/ExternalLink.vue'
import { REPLACE_NAME_VARIABLES } from '@/libs/utilities/replaceVariable'
import MessageAttachmentActions from '@/components/organisms/MessageAttachmentActions/MessageAttachmentActions.vue'
import KartePlannerCommentTemplateSelector from './KartePlannerCommentTemplateSelector.vue'

import type {
  KartePlannerCommentFormAvailabilityFragment,
  KartePlannerCommentFormViewerFragment,
} from './KartePlannerCommentForm.generated'
import { KartePlannerCommentUnavailableReason } from '@/types/schema.generated'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { getPlanVersion } from '@/features/plan/plan'

const placeholder = `記入例:
①まずはキャッチコピーを書きましょう！
例：失敗しない保険選びの話を聞いてみませんか？ /  どのようにして投資初心者が資産運用できたのか知りたくありませんか？

②挨拶文を描いたのちに「褒める/共感する」などの言葉を使って健康診断の結果に触れましょう！
例：○○さんの現状を健康診断の結果より確認いたしました。同世代同地域の方と比較して毎月貯蓄ができており素晴らしいですね！

③解決策と便益を訴求しましょう！
例：将来に向けた資産形成をする前にやるべきなのは「ライフプランシミュレーション」です。こちらを行うことで、将来に向けたお金の準備の可視化ができてお金の漠然とした不安が解消されます。

④自己紹介をしましょう！
これまでのキャリア、得意/専門分野、そして相談相手に対する想いなどを書きましょう

⑤選択肢も用意した質問をしておきましょう！
例：どのようなお金の相談をされたいですか？①生命保険について、②資産運用について、③相続について
`

const props = defineProps<{
  comment: string
  selections: string[]
  commentFormAvailability: KartePlannerCommentFormAvailabilityFragment
  viewer: KartePlannerCommentFormViewerFragment
}>()

const emit = defineEmits<{
  (event: 'update:comment', value: string): void
  (event: 'update:selections', value: string[]): void
  (event: 'postComment'): void
}>()

const isEditingTemplate = ref(false)

const localPlannerComment = computed({
  get(): string {
    return props.comment
  },
  set(value: string) {
    emit('update:comment', value)
  },
})

const localPlannerSelections = computed({
  get(): string[] {
    return props.selections
  },
  set(value: string[]) {
    emit('update:selections', value)
  },
})

const handleLoadTemplate = (body: string, selections: string[]) => {
  localPlannerComment.value =
    localPlannerComment.value !== ''
      ? `${localPlannerComment.value}\n${body}`
      : body

  // 選択肢については既存の内容をクリアしてしまってよい
  localPlannerSelections.value = selections
}

const postComment = () => {
  emit('postComment')
}

const isFormAvailable = computed(
  () => props.commentFormAvailability.availability
)

const unavailableReason = computed(
  () => props.commentFormAvailability.unavailableReason
)

const planVersion = computed(() => getPlanVersion(props.viewer.plan))
</script>

<style lang="scss" module>
.form {
  position: relative;
  display: flex;
  flex-direction: column;

  &__description {
    color: $color-text-and-object-medium-emphasis;
    font-size: 14px;
    line-height: 24px;

    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }

  &__template {
    padding-top: 16px;
    border-top: 1px solid $color-border-low-emphasis;

    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }

  &__textarea {
    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }

  &__button {
    &:nth-child(n + 2) {
      margin: 24px auto 0;
    }
  }

  &__replace {
    margin-top: 8px;
  }

  &__selections {
    &:nth-child(n + 2) {
      margin-top: 10px;
    }
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__title {
    color: $color-text-and-object-high-emphasis;
    font-size: 16px;
    font-weight: bold;
    line-height: 28px;
  }
}

.notice {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $color-text-and-object-high-emphasis;
  opacity: 0.9;

  &__text {
    padding: 16px;
    color: $color-text-and-object-high-emphasis-inverse;
    font-size: 20px;
    font-weight: bold;
    text-align: center;
  }
}

.replace {
  align-self: flex-start;
  padding: 16px;
  background-color: $color-surface-secondary;
  border-radius: 8px;
  color: $color-text-and-object-medium-emphasis;
  font-size: 14px;
  line-height: 24px;

  &__title {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-weight: bold;
  }

  code {
    padding: 0.2em 0.4em;
    background-color: $color-surface-tertiary;
    border-radius: 4px;
    font-size: 14px;
  }
}
</style>
